import type { Buffer } from "../model/buffer";
import { createBuffer } from "../model/buffer";
import type { Cursor } from "../model/cursor";
import type { RegisterContent } from "../model/registers";
import { firstNonBlankCol } from "../motions/basic";

export interface PutResult {
  readonly buffer: Buffer;
  readonly cursor: Cursor;
}

function putLinewise(
  buffer: Buffer,
  cursor: Cursor,
  content: readonly string[],
  before: boolean,
): PutResult {
  const insertAt = before ? cursor.line : cursor.line + 1;
  const lines = [...buffer.lines.slice(0, insertAt), ...content, ...buffer.lines.slice(insertAt)];
  const nextBuffer = createBuffer(lines);
  return {
    buffer: nextBuffer,
    cursor: { line: insertAt, col: firstNonBlankCol(nextBuffer, insertAt) },
  };
}

function putCharwise(
  buffer: Buffer,
  cursor: Cursor,
  content: readonly string[],
  before: boolean,
): PutResult {
  const line = buffer.lines[cursor.line] ?? "";
  const insertCol = before ? cursor.col : Math.min(cursor.col + 1, line.length);

  if (content.length === 1) {
    const text = content[0] ?? "";
    const newLine = line.slice(0, insertCol) + text + line.slice(insertCol);
    const lines = [...buffer.lines];
    lines[cursor.line] = newLine;
    const col = text.length > 0 ? insertCol + text.length - 1 : Math.max(insertCol - 1, 0);
    return { buffer: createBuffer(lines), cursor: { line: cursor.line, col } };
  }

  const prefix = line.slice(0, insertCol);
  const suffix = line.slice(insertCol);
  const middle = content.slice(1, -1);
  const firstLine = prefix + (content[0] ?? "");
  const lastLine = (content[content.length - 1] ?? "") + suffix;
  const lines = [
    ...buffer.lines.slice(0, cursor.line),
    firstLine,
    ...middle,
    lastLine,
    ...buffer.lines.slice(cursor.line + 1),
  ];
  return { buffer: createBuffer(lines), cursor: { line: cursor.line, col: insertCol } };
}

/** `p`（before=false）/ `P`（before=true）: レジスタ内容をカーソル位置へ貼り付ける。 */
export function applyPut(
  buffer: Buffer,
  cursor: Cursor,
  content: RegisterContent,
  before: boolean,
): PutResult {
  if (content.linewise) {
    return putLinewise(buffer, cursor, content.lines, before);
  }
  return putCharwise(buffer, cursor, content.lines, before);
}
